import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    selectDeals,
    songSummaryList,
    selectDealSubTypeList,
    selectDealSubDeals,
    selectContacts,
    selectDealDocuments,
} from '~/features/dashboard/store/selectors';
import moment from 'moment';
import css from './styles.module.scss';
import classNames from 'classnames';
import { orderBy, startCase, maxBy, minBy, filter } from 'lodash';
import { Button, Chip, Grid, Drawer, Typography, Divider, ListItemText, ListItem, Paper, Card, CardHeader } from '@material-ui/core';
import { Deal, SongSummary } from '~/features/dashboard/store/models';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getDealDetailsThunk, getDealDocumentsThunk, goToEditDealThunk } from '~/features/dashboard/store/actions.thunks';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Box, TextField } from '@material-ui/core';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import SongRightDrawer from '../song-rightdrawer';
import { getDealSongsSummaryThunk, getDealSubDealsSummaryThunk } from '~/features/dashboard/store/actions.thunks';
import { isDealFormEditing } from '~/features/dashboard/store/actions';
import SaveIcon from '@material-ui/icons/Save';
import { Controller, useForm } from 'react-hook-form';
import IconButton from '@material-ui/core/IconButton';
import { DEAL_TODODRAWER } from '~/features/dashboard/components/todo-deal-drawer';
import { DownloadButton } from '~/common/download-button';
import { VerticalPopver } from '../vertical-popover';

import { DealSubTypeList } from '~/models/deals/DealSubTypeList.dto';
import { PageBlock } from '~/features/dashboard/components/page-block';
import { CheckCircleOutlineOutlined as CheckCircleIcon, CancelOutlined as CancelIcon } from '@material-ui/icons';
import { KeyDatesTable } from '~/features/dashboard/components/key-dates-table';
import { axiosInstance } from '~/api/config/axios.config';
import { CollapsibleBlock } from '~/features/dashboard/components/collapsible-block';
import { PaginatedBlock } from '~/features/dashboard/components/paginated-block';
import { DealSongsSummaryDto } from '~/models/deals/DealSongsSummary.dto';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Avatar from '@material-ui/core/Avatar';
import { DealSummaryListDto } from '~/models/deals/DealsListItem.dto';
import SubDealsRightDrawer from '~/features/dashboard/components/subdeals-rightdrawer';
import { DealDocumentDto } from '~/models/deals/DealDocument.dto';
import DocumentsRightDrawer from '~/features/dashboard/components/documents-rightdrawer';
import CollapsibleSlider from '~/features/dashboard/components/collapsible-slider';
import { User } from '~/store/user/models';
import { DateFormat } from '~/common/date-format';

interface Description_props {
    contractRefNum: string;
    contractDescription: string;
}

interface DealDetailsProps {
    deal: Deal;
    dealSubTypeList: DealSubTypeList;
    projectId: number;
}

interface paymentDates_props {
    DatesBefore?: {
        Diff: number;
        Index: number;
        Date: any;
        IsPaid: any;
    }[];
    DatesAfter?: {
        Diff: number;
        Index: number;
        Date: any;
        IsPaid: any;
    }[];
}
interface noteData_props {
    id: number;
    gDealRef: string;
    notes: string;
    completed: number;
    isDelete: number;
}

/* ----- Term Line ----- */
function getMonthsWords(monthCount: any) {
    function getPlural(number: any, word: any) {
        return (number === 1 && word.one) || word.other;
    }

    const months = { one: 'month', other: 'months' },
        years = { one: 'year', other: 'years' },
        m = monthCount % 12,
        y = Math.floor(monthCount / 12),
        result = [];

    y && result.push(y + ' ' + getPlural(y, years));
    m && result.push(m + ' ' + getPlural(m, months));
    return result.join(' and ');
}
function monthDiff(d1: any, d2: any) {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
}
interface Term_props {
    isNoEndDate: boolean;
    startDate: string;
    endDate: any;
}
const TermComp: React.FC<Term_props> = ({ isNoEndDate, startDate, endDate }) => {
    return (
        <>
            {Boolean(isNoEndDate) ? (
                <Typography variant={'subtitle2'} className={'gray'} component="span">
                    Perpetuity
                </Typography>
            ) : startDate.trim() !== '' ? (
                <Typography variant={'subtitle2'} className={'gray'} component="span">
                    {getMonthsWords(monthDiff(new Date(startDate), new Date(endDate)))}
                </Typography>
            ) : (
                <Typography variant={'subtitle2'} className={'gray'} component="span">
                    Perpetuity
                </Typography>
            )}
        </>
    );
};
/* ---- End Term line ---- */

export type LinkedSongType = {
    gSongEntryId: number;
    assetCode: string;
    featuredArtists?: string;
    songArtistImage?: string;
    songArtist?: string;
    songName?: string;
    writingShare?: string;
    gMyWriterName?: string;
};

enum DRAWER_TYPES {
    NOTES,
    SONGS,
    DEALS,
    DOCUMENTS,
}

export const DealDetailsComponent: React.FC<DealDetailsProps> = ({ deal, dealSubTypeList, projectId }) => {
    const route = useAppRoute();
    const dispatch = useThunkDispatch();

    /* ---- States ---- */
    const [isDescriptionEdit, setIsDescriptionEdit] = React.useState(false);
    const [descriptionData, setDescriptionData] = React.useState(deal.description);
    const [paymentDatesList, setPaymentDatesList] = React.useState<paymentDates_props>();
    const [dealAcquisition, setDealAcquisition] = React.useState<{ Id: number; Text: string; value: string }[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isNoEndDate, setIsNoEndDate] = useState(false);
    const [notesData, setNotesData] = React.useState<noteData_props[]>([]);
    const [toDoData, setToDosData] = React.useState<noteData_props[]>([]);

    /* ---- Drawer ---- */
    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        if (drawerData.type === DRAWER_TYPES.SONGS) {
            getSongs();
        }
        if (drawerData.type === DRAWER_TYPES.DEALS) {
            getSubDeals();
        }
        if (drawerData.type === DRAWER_TYPES.DOCUMENTS) {
            getDocuments();
        }
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };
    const openSongsDrawer = (showLinked = false) => {
        openDrawer(DRAWER_TYPES.SONGS, { showLinked });
    };
    const openSubDealsDrawer = (showLinked = false) => {
        openDrawer(DRAWER_TYPES.DEALS, { showLinked });
    };
    const openDocumentsDrawer = (document?: DealDocumentDto) => {
        openDrawer(DRAWER_TYPES.DOCUMENTS, { document });
    };

    /* ---- Selectors ---- */
    const contacts = useSelector(selectContacts) || [];
    const documents: DealDocumentDto[] = useSelector(selectDealDocuments) || [];
    const songSummary: SongSummary[] = useSelector(songSummaryList) || [];
    const subDeals: DealSummaryListDto[] = useSelector(selectDealSubDeals) || [];
    const { keyDates2, royaltyRates, recoupableItems, assetCode } = deal;

    const linkedSubDeals = useMemo(() => {
        return subDeals.filter(d => d.linkedFlag === 1);
    }, [subDeals]);

    const getSongs = useCallback(() => {
        dispatch(getDealSongsSummaryThunk({ creatorId: Number(projectId), assetCode: assetCode }));
    }, [assetCode, dispatch, projectId]);

    const getSubDeals = useCallback(() => {
        dispatch(getDealSubDealsSummaryThunk({ creatorId: Number(projectId), assetCode: assetCode }));
    }, [assetCode, dispatch, projectId]);

    const getDocuments = useCallback(() => {
        dispatch(getDealDocumentsThunk({ assetCode: assetCode }));
    }, [assetCode, dispatch]);

    const { register, handleSubmit, setValue, control } = useForm<Description_props>({
        defaultValues: {
            contractDescription: deal.description,
            contractRefNum: assetCode,
        },
    });

    useEffect(() => {
        setDescriptionData(deal.description);
        setValue('contractDescription', deal.description);
    }, [deal.description, setValue]);

    useEffect(() => {
        // TODO: use selectors
        axiosInstance
            .get('Catalogue/projectcreator/getinvoiceslist', { params: { dealId: assetCode } })
            .then(({ data: { startDate, endDate, paymentDates, isNoEndDate } }) => {
                startDate && startDate !== '1900-01-01T00:00:00' && setStartDate(moment(startDate).format('MM/DD/YYYY'));
                endDate && endDate !== '1900-01-01T00:00:00' && setEndDate(moment(endDate).format('MM/DD/YYYY'));
                setIsNoEndDate(!!isNoEndDate || endDate === '1900-01-01T00:00:00');
                paymentDates && setPaymentDatesList(paymentDates);
            });

        axiosInstance.get('Catalogue/projectcreator/dealtodolist', { params: { gDealRef: assetCode } }).then(async ({ data }: any) => {
            setToDosData(filter(data, ['isDelete', 0]));
        });

        axiosInstance.get('Catalogue/projectcreator/dealnoteslist', { params: { gDealRef: assetCode } }).then(async ({ data }: any) => {
            setNotesData(filter(data, ['isDelete', 0]));
        });

        axiosInstance.get('Catalogue/projectReference/acquisitiontype/list').then(async ({ data: rawData }: any) => {
            const data = orderBy(
                rawData.map((element: any) => ({
                    ...element,
                    value: 0,
                })),
                [({ Text }) => (Text.toLowerCase() === 'other' ? 1 : 0), 'Text'],
                ['asc']
            );

            await setDealAcquisition(data);
        });

        dispatch(isDealFormEditing(true));
        getSongs();
        getSubDeals();
        getDocuments();
    }, [assetCode]); // eslint-disable-line

    const legalEntityData = useMemo(() => contacts.find(c => c.userId === deal.legalContact), [contacts, deal]);
    const contactEntityData = useMemo(() => contacts.find(c => c.userId === deal.companyContact), [contacts, deal]);

    const songDetailsArray: LinkedSongType[] = useMemo(() => {
        if (songSummary.length > 0) {
            const viewSong: any = songSummary.filter((t: SongSummary) => t.dealSongLinkFlag === 1);
            return viewSong.map(
                (item: DealSongsSummaryDto): LinkedSongType => ({
                    gSongEntryId: item.gSongEntryId,
                    assetCode: item.assetCode,
                    gMyWriterName: item.writingShare !== 0 ? String(item.writingShare) : 'Missing',
                    songArtist: item.songArtist ? item.songArtist : '',
                    songArtistImage: item.songArtistImage ? `${process.env.REACT_APP_ASSET}${item.songArtistImage}` : '',
                    songName: item.songName ? item.songName : '',
                    featuredArtists: 'feature artist ',
                })
            );
        }
        return [];
    }, [songSummary]);

    const getAcquisitionTypeName = useCallback(
        (acquisitionTypeId?: number): string => {
            if (!acquisitionTypeId || !dealAcquisition?.length) return '';
            return dealAcquisition.find(t => t.Id === acquisitionTypeId)?.Text || '';
        },
        [dealAcquisition]
    );

    // TODO: process all keyDates in one loop
    const keyDateRetention = useMemo(
        () =>
            keyDates2
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 2)
                .map((item: any) => ({ label: item.contractNotes, meta: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ' })),
        [keyDates2]
    );
    const keyDateAudit = useMemo(
        () =>
            keyDates2
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 3)
                .map((item: any) => ({ label: item.contractNotes, meta: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ' })),
        [keyDates2]
    );
    const keyDateOption = useMemo(
        () =>
            keyDates2
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 4)
                .map((item: any) => ({ label: item.contractNotes, meta: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ' })),
        [keyDates2]
    );
    const keyDateOther = useMemo(
        () =>
            keyDates2
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 6)
                .map((item: any) => ({ label: item.contractNotes, meta: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ' })),
        [keyDates2]
    );
    const keyDateAcquisition = useMemo(
        () =>
            keyDates2
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 7)
                .map((item: any) => ({
                    label: item.contractNotes,
                    meta: (
                        <Box display="flex" justifyContent="flex-end">
                            <Typography variant="body2" className="bold" style={{ paddingRight: '16px', whiteSpace: 'nowrap' }}>
                                {item.contractAcquisitionList?.map((t: any): string => getAcquisitionTypeName(t.contractAcquisitionTypeId)).join(', ')}
                            </Typography>
                            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
                                {item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' '}
                            </Typography>
                        </Box>
                    ),
                    date: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ',
                })),
        [keyDates2, getAcquisitionTypeName]
    );
    const keyDateAdvanceData = useMemo(
        () =>
            keyDates2 // contractNotesCurrency
                .filter((t: any) => t.contractNotes !== '' && t.entryDate !== '' && t.contractType === 1)
                .map((item: any) => ({
                    label: item.contractNotes,
                    meta: (
                        <Box display="flex" justifyContent="flex-end">
                            <Typography variant="body2" className="bold" style={{ paddingRight: '16px', whiteSpace: 'nowrap' }}>
                                {item.contractNotesCurrency} {item.contractNotesAmount}
                            </Typography>
                            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
                                {item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' '}
                            </Typography>
                        </Box>
                    ),
                    date: item.entryDate ? <DateFormat newDate={item.entryDate} /> : ' ',
                })),
        [keyDates2]
    );

    const dealSubTypes = useMemo(
        () => orderBy(dealSubTypeList[deal.dealType.toLowerCase()] || [], [({ name }) => (name.toLowerCase() === 'other' ? 1 : 0), 'name'], ['asc']),
        [deal.dealType, dealSubTypeList]
    );

    const [paymentDatePrevious, paymentDateNext] = useMemo(
        () => [
            maxBy(paymentDatesList?.DatesBefore || [], ({ Date: date }) => new Date(date).getTime()),
            minBy(paymentDatesList?.DatesAfter || [], ({ Date: date }) => new Date(date).getTime()),
        ],
        [paymentDatesList]
    );

    const onSubmitDescription = useCallback(
        (data: Description_props) => {
            setIsDescriptionEdit(false);
            setDescriptionData(data.contractDescription);
            axiosInstance.put('catalogue/projectCreator/dealupdatedescription', {
                contractRefNum: deal.assetCode,
                contractDescript: data.contractDescription,
            });
        },
        [dispatch] // eslint-disable-line
    );

    /* ---- GoTo functions ---- */
    const gotoDealSteps = async (dealStep: any) => {
        dispatch(goToEditDealThunk({ assetCode: assetCode, dealStep: dealStep }));
    };
    const goToPayment = useCallback(() => route(`/creators-list/${projectId}/dashboard/money/${assetCode}/edit-deal`), [projectId, assetCode, route]);
    const goToPreviewDeal = useCallback(() => route(`/creators-list/${projectId}/dashboard/deals/table/${assetCode}/details-preview`), [
        projectId,
        assetCode,
        route,
    ]);

    // TODO: use thunk
    const deleteDocument = (id: number): void => {
        axiosInstance.delete(`Catalogue/projectcreator/dealtag/delete/${id}`).then(() => {
            getDocuments();
        });
    };

    const getLegalEntityName = (entity: User): string => {
        if (entity.contactType === 0) {
            return (entity.firstName || entity.lastName) ? `${entity.firstName} ${entity.lastName}` : '...';
        }
        return entity.companyName;
    };

    return (
        <>
            <Grid container className={css.module} id="capture" spacing={0}>
                <Grid item xs={12} className={css.header}>
                    <Box display="flex" alignItems="center">
                        <Box display="flex" justifyContent="flex-start">
                            <Typography className="bold" variant="h5">
                                {startCase(deal.title)}
                            </Typography>
                            <VerticalPopver childClassName={css.editIcon} actionsList="edit" onClickEdit={() => gotoDealSteps(0)} />
                        </Box>
                        <Box display="flex" justifyContent="center" flexGrow="1">
                            <Typography className="bold" variant="h5">
                                {startCase(deal.dealType.toLowerCase())}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="right">
                            <div className={css.toggleBtn1}>
                                <DownloadButton className={css.downloadButton} onClick={goToPreviewDeal} buttonFor="preview" />
                            </div>
                            <div className={css.toggleBtn}>
                                <DownloadButton onClick={() => openDrawer(DRAWER_TYPES.NOTES)} className={css.downloadButton} buttonFor="save" />
                            </div>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} className={css.content} style={{ padding: '12px 0 12px 12px' }}>
                    <Grid container>
                        <Grid item xs={6}>
                            {/* ---- DEAL SUB TYPES ---- */}
                            <PageBlock
                                className={css.tagsBlock}
                                editAction={() => gotoDealSteps(1)}
                                titleComponent={
                                    <Typography variant="subtitle1" className="bold" style={{ fontSize: '1rem' }}>
                                        What rights do you have for this Deal?
                                    </Typography>
                                }
                            >
                                <div className={css.tags}>
                                    {dealSubTypes?.map(subType => (
                                        <Chip
                                            key={subType.id}
                                            label={subType.name}
                                            onDelete={() => {}}
                                            deleteIcon={
                                                deal.contractSubTypeList?.find(t => t.accStatusExtUseId === subType.id) ? (
                                                    <CheckCircleIcon style={{ color: 'green' }} />
                                                ) : (
                                                    <CancelIcon style={{ color: 'red' }} />
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </PageBlock>
                            {/* ---- CONTACTS / TERM / SIGN DATE ---- */}
                            <PageBlock title="contacts" editAction={() => gotoDealSteps(2)}>
                                {(legalEntityData || contactEntityData) && (
                                    <Paper className={classNames(css.paper, css.contactsWrapper)} elevation={1}>
                                        <Paper className={classNames(css.paper, css.contactsContent)}>
                                            <Grid container>
                                                <Grid item xs={6} style={{ paddingRight: '8px' }}>
                                                    <Card elevation={0} className={css.contactCard}>
                                                        <CardHeader
                                                            className={css.contactCardHeader}
                                                            avatar={
                                                                <AvatarGroup>
                                                                    <Avatar
                                                                        variant="square"
                                                                        className={css.avatar}
                                                                        alt={legalEntityData && getLegalEntityName(legalEntityData)}
                                                                        src={legalEntityData?.image}
                                                                    />
                                                                </AvatarGroup>
                                                            }
                                                            title="Legal Entity"
                                                            subheader={(legalEntityData && getLegalEntityName(legalEntityData)) || ''}
                                                        />
                                                    </Card>
                                                </Grid>
                                                <Grid item xs={6} style={{ paddingLeft: '8px' }}>
                                                    <Card elevation={0} className={css.contactCard}>
                                                        <CardHeader
                                                            className={css.contactCardHeader}
                                                            avatar={
                                                                <AvatarGroup>
                                                                    <Avatar
                                                                        variant="square"
                                                                        className={css.avatar}
                                                                        alt={contactEntityData?.companyName || ''}
                                                                        src={contactEntityData?.image}
                                                                    />
                                                                </AvatarGroup>
                                                            }
                                                            title="Deal Contact"
                                                            subheader={contactEntityData?.companyName || ''}
                                                        />
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                        {(startDate || deal.signatureDate) && (
                                            <Box className={css.contactsMeta}>
                                                {startDate && (
                                                    <Box width="100%" style={{ paddingBottom: deal.signatureDate ? '0' : '12px' }}>
                                                        <Typography variant={'subtitle1'} className={'bold'}>
                                                            Term
                                                        </Typography>
                                                        <Typography variant={'subtitle2'} className={'gray'} component="span">
                                                            <TermComp isNoEndDate={isNoEndDate} endDate={endDate} startDate={startDate} />
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {deal.signatureDate && (
                                                    <Box width="100%">
                                                        <Typography variant={'subtitle1'} className={'bold'}>
                                                            Deal Signed
                                                        </Typography>
                                                        <Typography variant={'subtitle2'} className={'gray'} component="span">
                                                            <DateFormat newDate={deal.signatureDate} />
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Paper>
                                )}
                            </PageBlock>
                            {/* ---- KEY DATES ---- */}
                            <PageBlock title="key dates" editAction={() => gotoDealSteps(4)}>
                                {keyDateAcquisition?.length ? <KeyDatesTable title="Acquisition" data={keyDateAcquisition} /> : ''}
                                {keyDateAdvanceData?.length ? <KeyDatesTable title="Advances" data={keyDateAdvanceData} /> : ''}
                                {keyDateAudit?.length ? <KeyDatesTable title="Audit" data={keyDateAudit} /> : ''}
                                {keyDateOption?.length ? <KeyDatesTable title="Option" data={keyDateOption} /> : ''}
                                {keyDateRetention?.length ? <KeyDatesTable title="Retention" data={keyDateRetention} /> : ''}
                                {keyDateOther?.length ? <KeyDatesTable title="Other" data={keyDateOther} /> : ''}
                            </PageBlock>
                            {/* ---- PAYMENTS ---- */}
                            <PageBlock title="payments" editAction={() => gotoDealSteps(3)}>
                                <Grid container spacing={3}>
                                    {paymentDatePrevious && (
                                        <Grid item xs={6}>
                                            <Button
                                                className={css.paymentBtn}
                                                onClick={goToPayment}
                                                endIcon={
                                                    paymentDatePrevious.IsPaid === 0 ? (
                                                        <CancelIcon fontSize="small" style={{ color: 'red' }} />
                                                    ) : (
                                                        <CheckCircleIcon fontSize="small" style={{ color: 'green' }} />
                                                    )
                                                }
                                            >
                                                <DateFormat newDate={paymentDatePrevious.Date} />
                                            </Button>
                                        </Grid>
                                    )}
                                    {paymentDateNext && (
                                        <Grid item xs={6}>
                                            <Button
                                                className={css.paymentBtn}
                                                onClick={goToPayment}
                                                endIcon={
                                                    paymentDateNext.IsPaid === 0 ? (
                                                        <CancelIcon fontSize="small" style={{ color: 'red' }} />
                                                    ) : (
                                                        <CheckCircleIcon fontSize="small" style={{ color: 'green' }} />
                                                    )
                                                }
                                            >
                                                <DateFormat newDate={paymentDateNext.Date} />
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            </PageBlock>
                            {/* ---- ROYALTY / RECOUPABLE ITEMS ---- */}
                            <PageBlock>
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <PageBlock title="Royalty rates" className={css.subPageBlock}>
                                            {royaltyRates?.length > 0 && (
                                                <Box className={classNames(css.dataList, css.dataListColor, css.dataList3Cols)}>
                                                    {royaltyRates.slice(0, 4).map((item: any, index) => (
                                                        <ListItem key={index}>
                                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                                <Box>{item.title || 'Headline Rate'}</Box>
                                                                <Box>
                                                                    <Box component="span" mr={1}>
                                                                        {item.region}
                                                                    </Box>
                                                                    <Box component="span" display="inline-block" minWidth="34px">
                                                                        {item.percent}%
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                    ))}
                                                    {royaltyRates.length > 4 && (
                                                        <CollapsibleBlock>
                                                            {royaltyRates.slice(4, royaltyRates.length).map((item: any, index) => (
                                                                <ListItem key={index}>
                                                                    <Box display="flex" justifyContent="space-between" width="100%">
                                                                        <Box>{item.title || 'Headline Rate'}</Box>
                                                                        <Box>
                                                                            <Box component="span" mr={1}>
                                                                                {item.region}
                                                                            </Box>
                                                                            <Box component="span" display="inline-block" minWidth="34px">
                                                                                {item.percent}%
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </ListItem>
                                                            ))}
                                                        </CollapsibleBlock>
                                                    )}
                                                </Box>
                                            )}
                                        </PageBlock>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <PageBlock title="Recoupable items" editAction={() => gotoDealSteps(5)} className={css.subPageBlock}>
                                            {recoupableItems?.length > 0 && (
                                                <Box className={classNames(css.dataList, css.dataListColor, css.dataList3Cols)}>
                                                    {recoupableItems.slice(0, 4).map((item: any, index) => (
                                                        <ListItem key={index}>
                                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                                <Box>{item.title || 'World'}</Box>
                                                                <Box>
                                                                    <Box component="span" mr={1}>
                                                                        {item.region}
                                                                    </Box>
                                                                    <Box component="span" display="inline-block" minWidth="34px">
                                                                        {item.percent}%
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                    ))}
                                                    {recoupableItems.length > 4 && (
                                                        <CollapsibleBlock>
                                                            {recoupableItems.slice(4, recoupableItems.length).map((item: any, index) => (
                                                                <ListItem key={index}>
                                                                    <Box display="flex" justifyContent="space-between" width="100%">
                                                                        <Box>{item.title || 'World'}</Box>
                                                                        <Box>
                                                                            <Box component="span" mr={1}>
                                                                                {item.region}
                                                                            </Box>
                                                                            <Box component="span" display="inline-block" minWidth="34px">
                                                                                {item.percent}%
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </ListItem>
                                                            ))}
                                                        </CollapsibleBlock>
                                                    )}
                                                </Box>
                                            )}
                                        </PageBlock>
                                    </Grid>
                                </Grid>
                            </PageBlock>
                        </Grid>
                        <Grid item xs={6}>
                            {/* ---- DESCRIPTION ---- */}
                            <PageBlock title="DESCRIPTION" editAction={() => setIsDescriptionEdit(!isDescriptionEdit)} marginTop={'-14px'}>
                                {isDescriptionEdit && (
                                    <Paper className={css.paper} elevation={0}>
                                        <form onSubmit={handleSubmit(onSubmitDescription)}>
                                            <Controller
                                                as={<TextField multiline fullWidth />}
                                                control={control}
                                                inputRef={register}
                                                defaultValue={descriptionData}
                                                name="contractDescription"
                                            />
                                            <TextField multiline fullWidth inputRef={register} name={'contractRefNum'} hidden />

                                            <div className={css.descriptionAction}>
                                                <IconButton aria-label="submit" type="submit">
                                                    <SaveIcon style={{ cursor: 'pointer' }} />
                                                </IconButton>
                                                <IconButton onClick={() => setIsDescriptionEdit(false)}>
                                                    <CancelIcon style={{ cursor: 'pointer' }} />
                                                </IconButton>
                                            </div>
                                        </form>
                                    </Paper>
                                )}
                                {!isDescriptionEdit && descriptionData && (
                                    <Paper className={css.paper} elevation={0}>
                                        <Typography variant={'body1'} className={classNames('typography gray', css.pre)} component="pre">
                                            {descriptionData}
                                        </Typography>
                                    </Paper>
                                )}
                            </PageBlock>
                            {/* ---- SONGS / DEALS ---- */}
                            {deal.dealType.toUpperCase() === 'ACQUISITION' ? (
                                <PageBlock
                                    className={css.songDealsBlock}
                                    title="Deals link"
                                    editActionComponent={
                                        <>
                                            <Button className={css.buttonYellow} onClick={() => openSubDealsDrawer(false)}>
                                                Add Deals
                                            </Button>
                                            <Button className={css.buttonYellow} onClick={() => openSubDealsDrawer(true)}>
                                                View Deals
                                            </Button>
                                        </>
                                    }
                                >
                                    {linkedSubDeals.length > 0 && (
                                        <PaginatedBlock
                                            className={css.connectedDeals}
                                            data={linkedSubDeals}
                                            itemsPerPage={4}
                                            keyName="assetCode"
                                            renderItem={(subDeal: DealSummaryListDto, key) => (
                                                <div key={key} className={css.companyCard}>
                                                    <AvatarGroup className={'avatars-group'} max={3}>
                                                        {/*<DescriptionOutlined />*/}
                                                        <Avatar className={css.avatarCustomImg} alt={subDeal.contractTitle} src={subDeal.contactImage ? `${process.env.REACT_APP_ASSET}${subDeal.contactImage}` : ''} />
                                                    </AvatarGroup>
                                                    <Typography variant={'subtitle1'} className={'bold gray dealTitle'} style={{ marginLeft: '1rem' }}>
                                                        {subDeal.contractTitle}
                                                    </Typography>
                                                </div>
                                            )}
                                        />
                                    )}
                                </PageBlock>
                            ) : (
                                <PageBlock
                                    title="Songs"
                                    editActionComponent={
                                        <>
                                            <Button className={css.buttonBlue} onClick={() => openSongsDrawer(false)}>
                                                Link Songs
                                            </Button>
                                            <Button className={css.buttonBlue} onClick={() => openSongsDrawer(true)}>
                                                View Songs
                                            </Button>
                                        </>
                                    }
                                >
                                    {songDetailsArray?.length > 0 && (
                                        <PaginatedBlock
                                            className={css.connectedDeals}
                                            itemsPerPage={4}
                                            keyName="assetCode"
                                            data={songDetailsArray}
                                            renderItem={(song: LinkedSongType, key) => (
                                                <div key={key} className={css.companyCard}>
                                                    <AvatarGroup className={'avatars-group'} max={3}>
                                                        <Avatar className={css.avatarCustomImg} alt={song.songArtist} src={song.songArtistImage} />
                                                    </AvatarGroup>
                                                    <Box ml={2}>
                                                        <Typography variant={'subtitle1'} className={css.dealTitle}>
                                                            {song.songName}
                                                        </Typography>
                                                        <Typography variant={'subtitle2'} className={css.dealSubtitle}>
                                                            {song.songArtist}
                                                        </Typography>
                                                    </Box>
                                                </div>
                                            )}
                                        />
                                    )}
                                </PageBlock>
                            )}
                            {/* ---- NOTES ---- */}
                            {notesData?.length > 0 && (
                                <PageBlock title="NOTES">
                                    <div className={classNames(css.dataList)}>
                                        {notesData.slice(0, 3).map((t: any) => (
                                            <ListItem key={t.id}>
                                                <ListItemText primary={t.notes} />
                                            </ListItem>
                                        ))}
                                        {notesData.length > 3 && (
                                            <CollapsibleBlock>
                                                {notesData.slice(3, notesData.length).map((t: any) => (
                                                    <ListItem key={t.id}>
                                                        <ListItemText primary={t.notes} />
                                                    </ListItem>
                                                ))}
                                            </CollapsibleBlock>
                                        )}
                                    </div>
                                </PageBlock>
                            )}
                            {/* ---- DOCUMENTS ---- */}
                            <PageBlock title="DOCUMENTS" editAction={openDocumentsDrawer}>
                                <CollapsibleSlider
                                    title="Contract"
                                    assets={documents.filter(d => d.assetTags === 'Contract')}
                                    deleteAsset={deleteDocument}
                                    editAsset={openDocumentsDrawer}
                                    expanded
                                />
                                <CollapsibleSlider
                                    title="Amendment"
                                    assets={documents.filter(d => d.assetTags === 'Amendment')}
                                    deleteAsset={deleteDocument}
                                    editAsset={openDocumentsDrawer}
                                    expanded
                                />
                                <CollapsibleSlider
                                    title="Other"
                                    assets={documents.filter(d => !['Amendment', 'Contract'].includes(d.assetTags as string))}
                                    deleteAsset={deleteDocument}
                                    editAsset={openDocumentsDrawer}
                                    expanded
                                />
                                {/* TODO: enable List View if necessary */}
                                {/*{documents.length > 0 && (*/}
                                {/*    <Box className={classNames(css.dataList)}>*/}
                                {/*        {documents.slice(0, 3).map((d: any) => (*/}
                                {/*            <ListItem key={d.id}>*/}
                                {/*                <Box width="100%" display="flex" flexDirection="column" justifyContent="center">*/}
                                {/*                    <Typography variant="subtitle2">*/}
                                {/*                        {d.assetName}*/}
                                {/*                        {d.assetVersion && <Typography component="span" variant="caption"> {d.assetVersion.trim()}</Typography>}*/}
                                {/*                    </Typography>*/}
                                {/*                    <Typography variant="caption">{d.assetTags}</Typography>*/}
                                {/*                </Box>*/}
                                {/*                <VerticalPopver*/}
                                {/*                    childClassName={css.editIcon}*/}
                                {/*                    editAction={() => openDocumentsDrawer(d)}*/}
                                {/*                    deleteAction={() => {*/}
                                {/*                        deleteDocument(d.id);*/}
                                {/*                    }}*/}
                                {/*                    downloadAction={() => {*/}
                                {/*                        downloadDocument(d.assetPath);*/}
                                {/*                    }}*/}
                                {/*                />*/}
                                {/*            </ListItem>*/}
                                {/*        ))}*/}
                                {/*        {documents.length > 3 && (*/}
                                {/*            <CollapsibleBlock>*/}
                                {/*                {documents.slice(3, documents.length).map((d: any) => (*/}
                                {/*                    <ListItem key={d.id}>*/}
                                {/*                        <Box width="100%" display="flex" flexDirection="column" justifyContent="center">*/}
                                {/*                            <Typography variant="subtitle2">*/}
                                {/*                                {d.assetName}*/}
                                {/*                                {d.assetVersion && <Typography component="span" variant="caption"> {d.assetVersion.trim()}</Typography>}*/}
                                {/*                            </Typography>*/}
                                {/*                            <Typography variant="caption">{d.assetTags}</Typography>*/}
                                {/*                        </Box>*/}
                                {/*                        <VerticalPopver*/}
                                {/*                            childClassName={css.editIcon}*/}
                                {/*                            editAction={() => openDocumentsDrawer(d)}*/}
                                {/*                            deleteAction={() => {*/}
                                {/*                                deleteDocument(d.id);*/}
                                {/*                            }}*/}
                                {/*                            downloadAction={() => {*/}
                                {/*                                downloadDocument(d.assetPath);*/}
                                {/*                            }}*/}
                                {/*                        />*/}
                                {/*                    </ListItem>*/}
                                {/*                ))}*/}
                                {/*            </CollapsibleBlock>*/}
                                {/*        )}*/}
                                {/*    </Box>*/}
                                {/*)}*/}
                            </PageBlock>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Drawer variant="persistent" anchor={`right`} open={drawerData.isOpen} id="drawer" className={css.rightDrawer}>
                <div style={{ overflowY: 'scroll', overflowX: 'hidden', height: '100vh' }}>
                    <Box className={css.closeDrawer}>
                        <IconButton onClick={closeDrawer}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                    </Box>
                    <Divider />
                    {drawerData.type === DRAWER_TYPES.NOTES && (
                        <DEAL_TODODRAWER
                            dealId={deal.assetCode}
                            notesData={notesData}
                            toDoData={toDoData}
                            setNotesData={setNotesData}
                            setToDosData={setToDosData}
                        />
                    )}
                    {drawerData.type === DRAWER_TYPES.SONGS && (
                        <SongRightDrawer
                            assetCode={assetCode}
                            projectId={projectId}
                            items={songSummary.filter(item => (drawerData.meta?.showLinked ? item.dealSongLinkFlag === 1 : !item.dealSongLinkFlag))}
                            showLinked={drawerData.meta?.showLinked}
                            onClose={closeDrawer}
                        />
                    )}
                    {drawerData.type === DRAWER_TYPES.DEALS && (
                        <SubDealsRightDrawer
                            assetCode={assetCode}
                            projectId={projectId}
                            items={subDeals.filter((item: DealSummaryListDto) => (drawerData.meta?.showLinked ? item.linkedFlag === 1 : !item.linkedFlag))}
                            showLinked={drawerData.meta?.showLinked}
                            onClose={closeDrawer}
                        />
                    )}
                    {drawerData.type === DRAWER_TYPES.DOCUMENTS && (
                        <DocumentsRightDrawer assetCode={assetCode} item={drawerData.meta.document} onClose={closeDrawer} />
                    )}
                </div>
            </Drawer>
        </>
    );
};

export const DealDetails: React.FC = () => {
    const { dealId } = useParams();

    const deals = useSelector(selectDeals);
    const deal = useMemo(() => deals.find(({ assetCode }) => assetCode === dealId), [dealId, deals]);
    const dealSubTypeList = useSelector(selectDealSubTypeList);
    const dispatch = useThunkDispatch();
    const match = useRouteMatch<{ creatorId: string }>();
    const projectId = useMemo(() => Number(match.params.creatorId), [match.params.creatorId]);
    useEffect(() => {
        if (dealId) {
            dispatch(getDealDetailsThunk({ assetCode: dealId, projectId: projectId }));
        }
    }, [dealId, dispatch]); // eslint-disable-line
    if (!deal) {
        return null;
    }

    return <DealDetailsComponent deal={deal} dealSubTypeList={dealSubTypeList} projectId={projectId} />;
};
